import { useReducer, useEffect, useCallback } from 'react';
import produce from 'immer';
import { getIdNumber } from 'utils/userIdRole.js';
import { removeUser } from 'utils/sameRole.js';

function init(value) {
  const selected = new Set(value.map(getIdNumber));
  return {
    users: value,
    selected,
    changed: false,
  };
}

function useSelectUsers(value, onChange) {
  const reducer = produce((draft, action) => {
    const { type, user } = action;
    const { selected, users } = draft;
    const userId = user && getIdNumber(user);
    switch (type) {
      case 'add':
        if (!selected.has(userId)) {
          selected.add(userId);
          users.push(user);
          draft.changed = true;
        }
        break;
      case 'remove':
        if (selected.has(userId)) {
          selected.delete(userId);
          draft.users = removeUser(users, user);
          draft.changed = true;
        }
        break;
      case 'update':
        draft.users = value;
        draft.selected = new Set(value.map(getIdNumber));
        draft.changed = false;
        break;
      default:
    }
  });
  const [state, dispatch] = useReducer(reducer, value, init);
  const { selected, changed, users } = state;
  useEffect(() => {
    onChange(users);
  }, [changed]); // eslint-disable-line react-hooks/exhaustive-deps
  const addUser = useCallback((user) => dispatch({ type: 'add', user }), [dispatch]);
  const deleteUser = useCallback((user) => dispatch({ type: 'remove', user }), [dispatch]);
  const isIncluded = useCallback((user) => selected.has(getIdNumber(user)), [selected]);
  const handleCheck = useCallback((user) => (ev) => dispatch({ type: ev.target.checked ? 'add' : 'remove', user }), [dispatch]);
  useEffect(() => {
    dispatch({ type: 'update' });
  }, [value]);
  return { addUser, deleteUser, handleCheck, isIncluded };
}

export default useSelectUsers;
